'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale } from 'next-intl';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import type { MarketIdentityData } from '@/components/market/identity/market-identity';
import { WinnersRowLiveline } from '@/components/markets/winners/winners-row-liveline';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Icon } from '@/components/ui/icon';
import { CircleArrowDown02IconSolid } from '@/components/ui/icon/glyphs';
import { StatusPill } from '@/components/ui/status-pill';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { formatPercentValue } from '@/lib/format/format-number';
import { FEED_CARD_HOVER_BACKGROUND, FEED_CARD_RESTING_OUTLINE } from '@/lib/ui/feed-card-chrome';
import { cn } from '@/lib/ui/utils';
import { winnerHueFromMint } from '@/lib/winners/winner-hue';

/**
 * One similar-winner — the fields the `useSimilarWinnersByMarket` row carries
 * that this card renders. A subset of `GraduatedMarket` (+ similarity); kept as
 * a local prop type so the card is a pure presentational leaf (NO SDK field
 * hooks — it renders OUTSIDE any `<Items>` context, unlike `WinnersRow`).
 */
export interface SimilarWinnerCardData extends MarketIdentityData {
  readonly mint: string;
  readonly title: string;
  readonly outcomeLabel: string;
  readonly outcomeImageUrl: string;
  readonly outcomeImageNsfw: boolean;
  readonly price: string;
  readonly marketCap: string;
  readonly change24hBps: string | null;
  readonly sparkline7d: readonly string[];
}

/** Signed-bps 24h-change pill. Tinted by sign; neutral on a value rounding to 0. */
function Change24hBadge(t0) {
  const $ = _c(14);
  const {
    bps,
    locale
  } = t0;
  if (bps === null || bps === "") {
    return null;
  }
  const pct = Number(bps) / 100;
  if (!Number.isFinite(pct)) {
    return null;
  }
  let isZero;
  let t1;
  if ($[0] !== locale || $[1] !== pct) {
    const abs = Math.abs(pct);
    isZero = Math.round(abs * 10) === 0;
    t1 = formatPercentValue(abs, locale, isZero ? 1 : 0);
    $[0] = locale;
    $[1] = pct;
    $[2] = isZero;
    $[3] = t1;
  } else {
    isZero = $[2];
    t1 = $[3];
  }
  const label = `${t1}%`;
  if (isZero) {
    let t2;
    if ($[4] !== label) {
      t2 = <span className="inline-flex w-fit shrink-0 items-center rounded-pill bg-background-tertiary px-2 py-1.5 text-caption text-foreground-secondary">{label}</span>;
      $[4] = label;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  const isUp = pct > 0;
  const t2 = isUp ? "success" : "destructive";
  const t3 = isUp && "rotate-180";
  let t4;
  if ($[6] !== t3) {
    t4 = cn("size-3 shrink-0", t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <Icon icon={CircleArrowDown02IconSolid} className={t4} />;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== label || $[11] !== t2 || $[12] !== t5) {
    t6 = <StatusPill tone={t2} size="sm" className="pl-1.5">{t5}{label}</StatusPill>;
    $[10] = label;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}

/**
 * Presentational similar-winner card for the winner-detail "Similar winners"
 * feed. Renders entirely from props (the enriched `useSimilarWinnersByMarket`
 * row) — it must NOT call `useGetWinnersField`, which throws outside a
 * `<Items>` list (sdk-list-rendering.md). Links to the winning token page.
 */
export function SimilarWinnerCard(t0) {
  const $ = _c(45);
  const {
    winner
  } = t0;
  const locale = useLocale();
  const {
    formatD6
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== winner.mint) {
    t1 = winnerHueFromMint(winner.mint);
    $[0] = winner.mint;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hue = t1;
  let t2;
  if ($[2] !== formatD6) {
    t2 = n => formatD6(String(Math.round(n)), "compact");
    $[2] = formatD6;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const formatMoney = t2;
  const href = `/winners/${winner.mint}`;
  const intent = useIntentRoutePrefetch(href);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("relative flex flex-col gap-3 rounded-card bg-background-primary px-4 py-4", FEED_CARD_HOVER_BACKGROUND, FEED_CARD_RESTING_OUTLINE);
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== href || $[6] !== intent || $[7] !== winner.outcomeLabel) {
    t4 = <Link href={href} prefetch={false} {...intent} aria-label={winner.outcomeLabel} className="absolute inset-0 rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-border-primary" />;
    $[5] = href;
    $[6] = intent;
    $[7] = winner.outcomeLabel;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = winner.outcomeImageUrl !== "" ? winner.outcomeImageUrl : null;
  let t6;
  if ($[9] !== hue || $[10] !== t5 || $[11] !== winner.mint || $[12] !== winner.outcomeImageNsfw) {
    t6 = <OptimizedImage variant="thumbnail" entity={{
      kind: "winner",
      mint: winner.mint,
      hue,
      imageUrl: t5,
      nsfw: winner.outcomeImageNsfw
    }} alt="" size="size-10" rounded="rounded-control" preview={false} />;
    $[9] = hue;
    $[10] = t5;
    $[11] = winner.mint;
    $[12] = winner.outcomeImageNsfw;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6 || $[15] !== winner.marketType) {
    t7 = <ThumbWithOrigin marketType={winner.marketType}>{t6}</ThumbWithOrigin>;
    $[14] = t6;
    $[15] = winner.marketType;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== winner.outcomeLabel) {
    t8 = <span className="truncate text-body font-medium text-foreground-secondary">{winner.outcomeLabel}</span>;
    $[17] = winner.outcomeLabel;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== winner.title) {
    t9 = <span className="line-clamp-1 text-caption text-foreground-tertiary">{winner.title}</span>;
    $[19] = winner.title;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <div className="flex min-w-0 flex-1 flex-col">{t8}{t9}</div>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== locale || $[25] !== winner.change24hBps) {
    t11 = <Change24hBadge bps={winner.change24hBps} locale={locale} />;
    $[24] = locale;
    $[25] = winner.change24hBps;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t7) {
    t12 = <div className="flex items-center gap-3">{t7}{t10}{t11}</div>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t7;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const t13 = Number(winner.marketCap);
  let t14;
  if ($[31] !== formatMoney || $[32] !== t13) {
    t14 = <span className="truncate text-heading-md font-semibold text-foreground-primary"><AnimatedNumber value={t13} format={formatMoney} duration={400} /></span>;
    $[31] = formatMoney;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== winner.change24hBps || $[35] !== winner.price || $[36] !== winner.sparkline7d) {
    t15 = <div className="h-9 w-24 shrink-0"><WinnersRowLiveline series={winner.sparkline7d} currentPriceD9={winner.price} change24hBps={winner.change24hBps} /></div>;
    $[34] = winner.change24hBps;
    $[35] = winner.price;
    $[36] = winner.sparkline7d;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14 || $[39] !== t15) {
    t16 = <div className="flex items-center justify-between gap-3">{t14}{t15}</div>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t12 || $[42] !== t16 || $[43] !== t4) {
    t17 = <div data-testid="similar-winner-card" className={t3}>{t4}{t12}{t16}</div>;
    $[41] = t12;
    $[42] = t16;
    $[43] = t4;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  return t17;
}